const FREQUENTLY_ADDED_STORAGE_KEY = 'cartDrawerFrequentlyAddedRemoved';

function readFrequentlyAddedRemovedKeys() {
  try {
    return JSON.parse(sessionStorage.getItem(FREQUENTLY_ADDED_STORAGE_KEY) || '[]');
  } catch {
    return [];
  }
}

function writeFrequentlyAddedRemovedKeys(keys) {
  sessionStorage.setItem(FREQUENTLY_ADDED_STORAGE_KEY, JSON.stringify(keys));
}

function setDrawerAddButtonLoading(button, isLoading) {
  if (!button) return;
  const spinner = button.querySelector('.loading__spinner');
  button.classList.toggle('loading', isLoading);
  if (spinner) spinner.classList.toggle('hidden', !isLoading);
}

class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
    this.addEventListener('click', this.onBundleSavingsClick.bind(this));
    this.addEventListener('click', this.onFrequentlyAddedClick.bind(this));
    this.addEventListener('change', this.onFrequentlyAddedChange.bind(this));
    this.addEventListener('click', this.onCompleteSetupClick.bind(this));
    this.addEventListener('change', this.onCompleteSetupChange.bind(this));
    this.addEventListener('click', this.onRecoveryClick.bind(this));
    this.addEventListener('change', this.onRecoveryChange.bind(this));
    this.purgeFrequentlyAddedFromStorage();
    this.setHeaderCartIconAccessibility();
  }

  /**
   * Replace cart line (product with bundle_savings metafield) with the bundle variant from the metaobject.
   */
  onBundleSavingsClick(event) {
    const button = event.target.closest('[data-bundle-savings-add]');
    if (!button || button.getAttribute('aria-disabled') === 'true') return;

    event.preventDefault();
    const line = parseInt(button.dataset.cartLine, 10);
    const variantId = parseInt(button.dataset.bundleVariantId, 10);
    const quantity = Math.max(1, parseInt(button.dataset.bundleQuantity, 10) || 1);

    if (!line || !variantId) return;

    button.setAttribute('aria-disabled', 'true');
    setDrawerAddButtonLoading(button, true);

    const sections = ['cart-drawer', 'cart-icon-bubble'];
    const sectionsUrl = window.location.pathname;

    const bodyRemove = JSON.stringify({
      line,
      quantity: 0,
      sections,
      sections_url: sectionsUrl,
    });

    fetch(`${routes.cart_change_url}`, { ...fetchConfig(), body: bodyRemove })
      .then((response) => response.text())
      .then((text) => JSON.parse(text))
      .then((changeState) => {
        if (changeState.errors) {
          const msg = typeof changeState.errors === 'string' ? changeState.errors : changeState.errors.join?.(', ') || '';
          throw new Error(msg || 'cart change');
        }

        const bodyAdd = JSON.stringify({
          items: [{ id: variantId, quantity }],
          sections: sections.join(','),
          sections_url: sectionsUrl,
        });

        return fetch(`${routes.cart_add_url}`, { ...fetchConfig(), body: bodyAdd }).then((r) => r.json());
      })
      .then((addState) => {
        if (addState.status) {
          throw new Error(addState.description || addState.message || 'cart add');
        }

        publish(PUB_SUB_EVENTS.cartUpdate, {
          source: 'bundle-savings',
          cartData: addState,
          variantId: String(variantId),
        });

        this.renderContents(addState);
        this.classList.toggle('is-empty', addState.item_count === 0);
      })
      .catch((e) => {
        console.error(e);
        const errors = document.getElementById('CartDrawer-CartErrors');
        if (errors) errors.textContent = window.cartStrings.error;
      })
      .finally(() => {
        setDrawerAddButtonLoading(button, false);
        button.removeAttribute('aria-disabled');
      });
  }

  purgeFrequentlyAddedFromStorage() {
    const removed = readFrequentlyAddedRemovedKeys();
    if (!removed.length) {
      this.updateFrequentlyAddedSectionVisibility();
      return;
    }
    this.querySelectorAll('[data-frequently-added-card]').forEach((card) => {
      const key = card.dataset.rowKey;
      if (key && removed.includes(key)) {
        card.remove();
      }
    });
    this.updateFrequentlyAddedSectionVisibility();
  }

  updateFrequentlyAddedSectionVisibility() {
    const section = this.querySelector('[data-frequently-added-section]');
    if (!section) return;
    const items = section.querySelector('[data-frequently-added-items]');
    if (!items || items.children.length === 0) {
      section.classList.add('hidden');
    } else {
      section.classList.remove('hidden');
    }
  }

  frequentlyAddedParseJson(card) {
    const el = card.querySelector('.js-frequently-added-json');
    if (!el) return null;
    try {
      return JSON.parse(el.textContent.trim());
    } catch (e) {
      return null;
    }
  }

  frequentlyAddedUpdatePriceDisplay(card) {
    const data = this.frequentlyAddedParseJson(card);
    if (!data) return;

    const variantSel = card.querySelector('[data-variant-select]');
    const variantId = variantSel ? variantSel.value : String(data.variants[0]?.id);
    const v = data.variants.find((x) => String(x.id) === String(variantId));
    if (!v) return;

    const purchase = card.querySelector('[data-frequently-added-purchase]');
    const planSel = card.querySelector('[data-selling-plan-select]');
    const purchaseVisible = purchase && !purchase.hasAttribute('hidden');

    let priceFormatted = v.price_formatted;
    let compareFormatted = v.compare_at_formatted || '';

    const plans = v.selling_plans || [];
    if (purchaseVisible && planSel && planSel.value) {
      const plan = plans.find((p) => String(p.id) === String(planSel.value));
      if (plan && plan.price_formatted) {
        priceFormatted = plan.price_formatted;
        compareFormatted = plan.compare_at_formatted || '';
      }
    }

    const priceEl = card.querySelector('[data-frequently-added-price]');
    const compareEl = card.querySelector('[data-frequently-added-compare]');
    if (priceEl) priceEl.textContent = priceFormatted;
    if (compareEl) {
      if (compareFormatted) {
        compareEl.textContent = compareFormatted;
        compareEl.classList.remove('hidden');
      } else {
        compareEl.textContent = '';
        compareEl.classList.add('hidden');
      }
    }
  }

  setFrequentlyAddedPurchaseMode(card, mode) {
    const purchase = card.querySelector('[data-frequently-added-purchase]');
    const planSel = card.querySelector('[data-selling-plan-select]');
    if (!purchase || !planSel) {
      this.frequentlyAddedUpdatePriceDisplay(card);
      return;
    }

    purchase.dataset.purchaseMode = mode;
    if (mode === 'onetime') planSel.value = '';

    this.frequentlyAddedUpdatePriceDisplay(card);
  }

  frequentlyAddedApplyVariant(card, variantId) {
    const data = this.frequentlyAddedParseJson(card);
    if (!data) return;
    const v = data.variants.find((x) => String(x.id) === String(variantId));
    if (!v) return;

    const imgEl = card.querySelector('[data-frequently-added-img]');
    if (imgEl) {
      if (v.featured_media_url) {
        if (imgEl.tagName === 'IMG') {
          imgEl.src = v.featured_media_url;
          imgEl.classList.remove('hidden');
        }
      } else if (imgEl.tagName === 'IMG') {
        imgEl.removeAttribute('src');
        imgEl.classList.add('hidden');
      }
    }

    const purchase = card.querySelector('[data-frequently-added-purchase]');
    const planSel = card.querySelector('[data-selling-plan-select]');
    if (!purchase || !planSel) {
      this.frequentlyAddedUpdatePriceDisplay(card);
      return;
    }

    const plans = v.selling_plans || [];
    if (plans.length === 0) {
      purchase.hidden = true;
      purchase.classList.add('hidden');
      const addBtnOnly = card.querySelector('[data-frequently-added-add]');
      if (addBtnOnly) addBtnOnly.disabled = !v.available;
      this.frequentlyAddedUpdatePriceDisplay(card);
      return;
    }

    purchase.hidden = false;
    purchase.classList.remove('hidden');
    planSel.innerHTML = '';
    const onetime = document.createElement('option');
    onetime.value = '';
    onetime.textContent = data.oneTimeLabel || '';
    planSel.appendChild(onetime);
    plans.forEach((p) => {
      const o = document.createElement('option');
      o.value = String(p.id);
      o.textContent = p.name;
      planSel.appendChild(o);
    });

    let matchDefault = null;
    if (data.defaultSellingPlanId != null) {
      matchDefault = plans.find((p) => String(p.id) === String(data.defaultSellingPlanId));
    }

    if (matchDefault) {
      planSel.value = String(matchDefault.id);
      this.setFrequentlyAddedPurchaseMode(card, 'subscribe');
    } else {
      this.setFrequentlyAddedPurchaseMode(card, 'onetime');
    }

    const addBtn = card.querySelector('[data-frequently-added-add]');
    if (addBtn) addBtn.disabled = !v.available;

    this.frequentlyAddedUpdatePriceDisplay(card);
  }

  onFrequentlyAddedClick(event) {
    if (event.target.closest('[data-complete-setup-card]')) return;

    const addBtn = event.target.closest('[data-frequently-added-add]');
    if (addBtn && this.contains(addBtn)) {
      event.preventDefault();
      this.frequentlyAddedSubmitAdd(addBtn);
    }
  }

  onFrequentlyAddedChange(event) {
    if (event.target.closest('[data-complete-setup-card]')) return;
    const variantSel = event.target.closest('[data-variant-select]');
    if (variantSel && this.contains(variantSel)) {
      const card = variantSel.closest('[data-frequently-added-card]');
      if (card) this.frequentlyAddedApplyVariant(card, variantSel.value);
      return;
    }

    const planSel = event.target.closest('[data-selling-plan-select]');
    if (planSel && this.contains(planSel)) {
      const card = planSel.closest('[data-frequently-added-card]');
      if (card) this.setFrequentlyAddedPurchaseMode(card, planSel.value ? 'subscribe' : 'onetime');
    }
  }

  frequentlyAddedSubmitAdd(button) {
    const card = button.closest('[data-frequently-added-card]');
    if (!card || button.getAttribute('aria-disabled') === 'true') return;

    const variantSel = card.querySelector('[data-variant-select]');
    const variantId = parseInt(variantSel ? variantSel.value : this.frequentlyAddedParseJson(card)?.variants?.[0]?.id, 10);
    if (!variantId) return;

    const purchase = card.querySelector('[data-frequently-added-purchase]');
    const planSel = card.querySelector('[data-selling-plan-select]');
    const purchaseVisible = purchase && !purchase.hasAttribute('hidden');
    let sellingPlan = null;
    if (purchaseVisible && planSel && planSel.value) {
      sellingPlan = parseInt(planSel.value, 10);
    }

    const item = { id: variantId, quantity: 1 };
    if (sellingPlan) item.selling_plan = sellingPlan;

    button.setAttribute('aria-disabled', 'true');
    setDrawerAddButtonLoading(button, true);

    const sections = ['cart-drawer', 'cart-icon-bubble'];
    const sectionsUrl = window.location.pathname;
    const bodyAdd = JSON.stringify({
      items: [item],
      sections: sections.join(','),
      sections_url: sectionsUrl,
    });

    fetch(`${routes.cart_add_url}`, { ...fetchConfig(), body: bodyAdd })
      .then((r) => r.json())
      .then((addState) => {
        if (addState.status) {
          throw new Error(addState.description || addState.message || 'cart add');
        }

        const rowKey = card.dataset.rowKey;
        if (rowKey) {
          const removed = readFrequentlyAddedRemovedKeys();
          if (!removed.includes(rowKey)) {
            removed.push(rowKey);
            writeFrequentlyAddedRemovedKeys(removed);
          }
        }

        publish(PUB_SUB_EVENTS.cartUpdate, {
          source: 'frequently-added',
          cartData: addState,
          variantId: String(variantId),
        });

        this.renderContents(addState);
        this.classList.toggle('is-empty', addState.item_count === 0);
      })
      .catch((e) => {
        console.error(e);
        const errors = document.getElementById('CartDrawer-CartErrors');
        if (errors) errors.textContent = window.cartStrings.error;
      })
      .finally(() => {
        setDrawerAddButtonLoading(button, false);
        button.removeAttribute('aria-disabled');
      });
  }

  recoveryParseJson(card) {
    const el = card.querySelector('.js-recovery-json');
    if (!el) return null;
    try {
      return JSON.parse(el.textContent);
    } catch {
      return null;
    }
  }

  recoveryApplyVariant(card, variantId) {
    const data = this.recoveryParseJson(card);
    if (!data) return;
    const variant = data.variants.find((v) => String(v.id) === String(variantId));
    if (!variant) return;

    const img = card.querySelector('[data-recovery-img]');
    if (img && img.tagName === 'IMG' && variant.featured_media_url) {
      img.src = variant.featured_media_url;
    }

    const price = card.querySelector('[data-recovery-price]');
    if (price) price.textContent = variant.offer_formatted;

    const compare = card.querySelector('[data-recovery-compare]');
    if (compare) compare.textContent = variant.list_formatted;

    const addBtn = card.querySelector('[data-recovery-add]');
    if (addBtn) addBtn.disabled = !variant.available;
  }

  onRecoveryChange(event) {
    const variantSel = event.target.closest('[data-recovery-variant-select]');
    if (!variantSel || !this.contains(variantSel)) return;
    const card = variantSel.closest('[data-recovery-card]');
    if (card) this.recoveryApplyVariant(card, variantSel.value);
  }

  onRecoveryClick(event) {
    const addBtn = event.target.closest('[data-recovery-add]');
    if (!addBtn || !this.contains(addBtn)) return;
    event.preventDefault();
    this.recoverySubmitAdd(addBtn);
  }

  recoverySubmitAdd(button) {
    const card = button.closest('[data-recovery-card]');
    if (!card || button.getAttribute('aria-disabled') === 'true') return;

    const variantSel = card.querySelector('[data-recovery-variant-select]');
    const variantId = parseInt(variantSel ? variantSel.value : this.recoveryParseJson(card)?.variants?.[0]?.id, 10);
    if (!variantId) return;

    button.setAttribute('aria-disabled', 'true');
    setDrawerAddButtonLoading(button, true);

    const sections = ['cart-drawer', 'cart-icon-bubble'];
    const bodyAdd = JSON.stringify({
      items: [{ id: variantId, quantity: 1 }],
      sections: sections.join(','),
      sections_url: window.location.pathname,
    });

    fetch(`${routes.cart_add_url}`, { ...fetchConfig(), body: bodyAdd })
      .then((r) => r.json())
      .then((addState) => {
        if (addState.status) {
          throw new Error(addState.description || addState.message || 'cart add');
        }

        publish(PUB_SUB_EVENTS.cartUpdate, {
          source: 'cart-recovery',
          cartData: addState,
          variantId: String(variantId),
        });

        this.renderContents(addState);
        this.classList.toggle('is-empty', addState.item_count === 0);
      })
      .catch((e) => {
        console.error(e);
        const errors = document.getElementById('CartDrawer-CartErrors');
        if (errors) errors.textContent = window.cartStrings.error;
      })
      .finally(() => {
        setDrawerAddButtonLoading(button, false);
        button.removeAttribute('aria-disabled');
      });
  }

  completeSetupParseJson(card) {
    const el = card.querySelector('.js-complete-setup-json');
    if (!el) return null;
    try {
      return JSON.parse(el.textContent.trim());
    } catch (e) {
      return null;
    }
  }

  completeSetupResolveCard(el) {
    return el ? el.closest('[data-complete-setup-card]') : null;
  }

  completeSetupResolveVariantId(checkbox, card) {
    const data = this.completeSetupParseJson(card);
    const variantSel = card.querySelector('[data-complete-setup-variant-select]');

    // Selector is the source of truth when present (checkbox data-* is only the initial SSR value).
    if (variantSel?.value) {
      const id = parseInt(variantSel.value, 10);
      if (checkbox) checkbox.dataset.completeSetupVariantId = String(id);
      return id;
    }

    const fromCheckbox = checkbox?.dataset?.completeSetupVariantId;
    if (fromCheckbox) return parseInt(fromCheckbox, 10);

    if (data?.defaultVariantId) return parseInt(String(data.defaultVariantId), 10);

    if (data?.variants?.[0]?.id) return parseInt(String(data.variants[0].id), 10);

    return NaN;
  }

  completeSetupIntervalLabel(plan, data) {
    const key = plan && plan.interval_suffix_key ? plan.interval_suffix_key : 'month';
    const i18n = data.i18n || {};
    return i18n[`interval_${key}`] || i18n.interval_month || 'month';
  }

  completeSetupUpdatePriceDisplay(card) {
    const data = this.completeSetupParseJson(card);
    if (!data) return;

    const variantSel = card.querySelector('[data-complete-setup-variant-select]');
    const variantId = variantSel ? variantSel.value : String(data.variants[0]?.id);
    const v = data.variants.find((x) => String(x.id) === String(variantId));
    if (!v) return;

    const cb = card.querySelector('[data-complete-setup-checkbox]');
    if (cb) cb.dataset.completeSetupVariantId = String(variantId);

    const purchase = card.querySelector('[data-complete-setup-purchase]');
    const planSel = card.querySelector('[data-complete-setup-plan-select]');
    const purchaseVisible = purchase && !purchase.hasAttribute('hidden');
    const mode = purchaseVisible && planSel && planSel.value ? 'subscribe' : 'onetime';

    let priceFormatted = v.price_formatted;
    const plans = v.selling_plans || [];
    let activePlan = null;
    if (mode === 'subscribe') {
      activePlan = plans.find((p) => String(p.id) === String(planSel.value));
      if (activePlan && activePlan.price_formatted) {
        priceFormatted = activePlan.price_formatted;
      }
    }

    const priceEl = card.querySelector('[data-complete-setup-price]');
    const intervalEl = card.querySelector('[data-complete-setup-interval]');
    if (priceEl) priceEl.textContent = priceFormatted;
    if (intervalEl) {
      if (mode === 'subscribe' && activePlan) {
        const label = this.completeSetupIntervalLabel(activePlan, data);
        intervalEl.textContent = `/ ${label}`;
      } else {
        intervalEl.textContent = '';
      }
    }
  }

  setCompleteSetupPurchaseMode(card, mode) {
    const purchase = card.querySelector('[data-complete-setup-purchase]');
    const planSel = card.querySelector('[data-complete-setup-plan-select]');
    if (!purchase || !planSel) {
      this.completeSetupUpdatePriceDisplay(card);
      return;
    }

    purchase.dataset.purchaseMode = mode;

    this.completeSetupUpdatePriceDisplay(card);
  }

  completeSetupApplyVariant(card, variantId) {
    const data = this.completeSetupParseJson(card);
    if (!data) return;
    const v = data.variants.find((x) => String(x.id) === String(variantId));
    if (!v) return;

    const imgEl = card.querySelector('[data-complete-setup-img]');
    if (imgEl) {
      if (v.featured_media_url) {
        if (imgEl.tagName === 'IMG') {
          imgEl.src = v.featured_media_url;
          imgEl.classList.remove('hidden');
        }
      } else if (imgEl.tagName === 'IMG') {
        imgEl.removeAttribute('src');
        imgEl.classList.add('hidden');
      }
    }

    const purchase = card.querySelector('[data-complete-setup-purchase]');
    const planSel = card.querySelector('[data-complete-setup-plan-select]');
    if (!purchase || !planSel) {
      this.completeSetupUpdatePriceDisplay(card);
      return;
    }

    const plans = v.selling_plans || [];
    if (plans.length === 0) {
      purchase.hidden = true;
      purchase.classList.add('hidden');
      const cb = card.querySelector('[data-complete-setup-checkbox]');
      if (cb) {
        cb.dataset.completeSetupVariantId = String(variantId);
        cb.disabled = !v.available;
      }
      this.completeSetupUpdatePriceDisplay(card);
      return;
    }

    purchase.hidden = false;
    purchase.classList.remove('hidden');
    planSel.innerHTML = '';
    plans.forEach((p) => {
      const o = document.createElement('option');
      o.value = String(p.id);
      o.textContent = p.name;
      planSel.appendChild(o);
    });

    let matchDefault = null;
    if (data.defaultSellingPlanId != null) {
      matchDefault = plans.find((p) => String(p.id) === String(data.defaultSellingPlanId));
    }
    if (!matchDefault) matchDefault = plans[0];

    planSel.value = String(matchDefault.id);
    this.setCompleteSetupPurchaseMode(card, 'subscribe');

    const cb = card.querySelector('[data-complete-setup-checkbox]');
    if (cb) {
      cb.dataset.completeSetupVariantId = String(variantId);
      cb.disabled = !v.available;
    }

    this.completeSetupUpdatePriceDisplay(card);
  }

  onCompleteSetupClick(event) {
    if (!event.target.closest('[data-complete-setup-card]')) return;

    const addBtn = event.target.closest('[data-complete-setup-add]');
    if (addBtn && this.contains(addBtn)) {
      event.preventDefault();
      const card = addBtn.closest('[data-complete-setup-card]');
      const checkbox = card?.querySelector('[data-complete-setup-checkbox]');
      if (!checkbox || addBtn.getAttribute('aria-disabled') === 'true') return;
      addBtn.setAttribute('aria-disabled', 'true');
      setDrawerAddButtonLoading(addBtn, true);
      checkbox.checked = true;
      const done = this.completeSetupSubmitAdd(checkbox);
      Promise.resolve(done).finally(() => {
        setDrawerAddButtonLoading(addBtn, false);
        addBtn.removeAttribute('aria-disabled');
      });
      return;
    }

  }

  onCompleteSetupChange(event) {
    const checkbox = event.target.closest('[data-complete-setup-checkbox]');
    if (checkbox && this.contains(checkbox)) {
      if (!checkbox.checked) return;
      this.completeSetupSubmitAdd(checkbox);
      return;
    }

    const variantSel = event.target.closest('[data-complete-setup-variant-select]');
    if (variantSel && this.contains(variantSel)) {
      const card = this.completeSetupResolveCard(variantSel);
      if (card) this.completeSetupApplyVariant(card, variantSel.value);
      return;
    }

    const planSel = event.target.closest('[data-complete-setup-plan-select]');
    if (planSel && this.contains(planSel)) {
      const card = planSel.closest('[data-complete-setup-card]');
      if (card) this.setCompleteSetupPurchaseMode(card, 'subscribe');
    }
  }

  completeSetupSubmitAdd(checkbox) {
    const card = this.completeSetupResolveCard(checkbox);
    if (!card || checkbox.getAttribute('aria-disabled') === 'true') return;

    const variantId = this.completeSetupResolveVariantId(checkbox, card);
    if (!variantId) {
      checkbox.checked = false;
      return;
    }

    const purchase = card.querySelector('[data-complete-setup-purchase]');
    const planSel = card.querySelector('[data-complete-setup-plan-select]');
    const purchaseVisible = purchase && !purchase.hasAttribute('hidden');
    let sellingPlan = null;
    if (purchaseVisible && planSel && planSel.value) {
      sellingPlan = parseInt(planSel.value, 10);
    }

    const item = { id: variantId, quantity: 1 };
    if (sellingPlan) item.selling_plan = sellingPlan;

    checkbox.setAttribute('aria-disabled', 'true');
    checkbox.disabled = true;

    const sections = ['cart-drawer', 'cart-icon-bubble'];
    const sectionsUrl = window.location.pathname;
    const bodyAdd = JSON.stringify({
      items: [item],
      sections: sections.join(','),
      sections_url: sectionsUrl,
    });

    return fetch(`${routes.cart_add_url}`, { ...fetchConfig(), body: bodyAdd })
      .then((r) => r.json())
      .then((addState) => {
        if (addState.status) {
          throw new Error(addState.description || addState.message || 'cart add');
        }

        publish(PUB_SUB_EVENTS.cartUpdate, {
          source: 'complete-setup',
          cartData: addState,
          variantId: String(variantId),
        });

        this.renderContents(addState);
        this.classList.toggle('is-empty', addState.item_count === 0);
      })
      .catch((e) => {
        console.error(e);
        checkbox.checked = false;
        const errors = document.getElementById('CartDrawer-CartErrors');
        if (errors) errors.textContent = window.cartStrings.error;
      })
      .finally(() => {
        checkbox.removeAttribute('aria-disabled');
        checkbox.disabled = false;
      });
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    if (!cartLink) return;

    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');
    cartLink.addEventListener('click', (event) => {
      event.preventDefault();
      this.open(cartLink);
    });
    cartLink.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }

  open(triggeredBy) {
    if (triggeredBy) this.setActiveElement(triggeredBy);
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);
    // here the animation doesn't seem to always get triggered. A timeout seem to help
    setTimeout(() => {
      this.classList.add('animate', 'active');
      this.querySelectorAll('swiper-slider').forEach((slider) => slider.swiperInstance?.update());
    });

    this.addEventListener(
      'transitionend',
      () => {
        const containerToTrapFocusOn = this.classList.contains('is-empty')
          ? this.querySelector('.drawer__inner-empty')
          : document.getElementById('CartDrawer');
        const focusElement = this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');
        trapFocus(containerToTrapFocusOn, focusElement);
      },
      { once: true }
    );

    document.body.classList.add('overflow-hidden');
  }

  close() {
    this.classList.remove('active');
    removeTrapFocus(this.activeElement);
    document.body.classList.remove('overflow-hidden');
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if (cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  renderContents(parsedState) {
    this.querySelector('.drawer__inner').classList.contains('is-empty') &&
      this.querySelector('.drawer__inner').classList.remove('is-empty');
    this.productId = parsedState.id;
    this.getSectionsToRender().forEach((section) => {
      const sectionElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);

      if (!sectionElement) return;
      sectionElement.innerHTML = this.getSectionInnerHTML(parsedState.sections[section.id], section.selector);
    });

    this.purgeFrequentlyAddedFromStorage();

    setTimeout(() => {
      this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
      this.open();
    });
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer',
      },
      {
        id: 'cart-icon-bubble',
      },
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__inner',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
    ];
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);
